import * as Yup from "yup";
import i18next from "../../../i18n";
import {
  eanValidator,
  globalMaterialNameValidator,
  measurementUnitValidator,
  customMeasurementUnitValidator,
  deliveryPlaceValidator,
  detailsValidator,
} from "../../../components/NewMaterialForm/newMaterialFormValidationSchema";

/**
 * Values React Hook Form manages for the inline "create material" modal reached from the
 * New-Purchase-Request composer / per-item autocomplete. One shape shared by both "kind"s the type
 * toggle offers (`GOOD` / `SERVICE`), same pattern as the standalone create-material page
 * (`components/NewMaterialForm`).
 *
 * The field-level validators (`ean`/`globalMaterialName`/`measurementUnit`/`customMeasurementUnit`/
 * `deliveryPlace`/`details`) are REUSED from `newMaterialFormValidationSchema.ts` rather than
 * re-typed here — the two create-material surfaces validate the identical fields and must not drift
 * on wording. What this modal adds on top is `ownerId` (the material owner selector the composer's
 * modal has and the standalone page does not), required exactly as the pre-Carbon modal's own
 * `if (!ean.trim() || !name.trim() || !ownerId)` guard enforced it.
 */
export interface NewMaterialModalValues {
  ean: string;
  globalMaterialName: string;
  ownerId: string;
  measurementUnit: string;
  customMeasurementUnit: string;
  deliveryPlace: string;
  details: string;
  brand: string;
  deliveryFormat: string;
  comments: string;
}

export const ownerIdValidator = Yup.string().required(
  i18next.t("newMaterialForm.ownerRequired")
);

/**
 * Goods require ean + name + owner + a measurement unit + a delivery place — exactly the
 * pre-Carbon modal's two-stage guard (`ean/name/owner` first, then `finalUnit/deliveryPlaceId` for
 * a non-service). `TO_BE_AGREED` is a legitimate, non-empty `deliveryPlace` value and so passes the
 * required rule, matching the pre-Carbon behaviour.
 */
export const newMaterialModalGoodsSchema = Yup.object({
  ean: eanValidator,
  globalMaterialName: globalMaterialNameValidator,
  ownerId: ownerIdValidator,
  measurementUnit: measurementUnitValidator,
  customMeasurementUnit: customMeasurementUnitValidator,
  deliveryPlace: deliveryPlaceValidator,
  details: detailsValidator,
  brand: Yup.string(),
  deliveryFormat: Yup.string(),
  comments: Yup.string(),
});

/**
 * A service only validates/sends ean + name + owner — the modal strips every goods-only field from
 * the payload for a service, same as the pre-Carbon modal's own service branch.
 */
export const newMaterialModalServiceSchema = Yup.object({
  ean: eanValidator,
  globalMaterialName: globalMaterialNameValidator,
  ownerId: ownerIdValidator,
});
